import {
  escapeHtml,
  staffInitials,
  minutesFromMidnight,
  isoFromMinutesOnDate,
  statusCssVariable,
} from "../utils";

// DayView renders a resource-based day grid: time column on the left, one column per staff,
// 30-minute rows. Events are absolute-positioned inside per-staff layers.
//
// It is a "dumb" view — it only renders and dispatches synthetic events through the
// CalendarScheduler instance it was given. Mutation is the consumer's job.
export class DayView {
  constructor(scheduler) {
    this.scheduler = scheduler;
    this.element = null;
    this.eventLayers = new Map(); // staff_id -> HTMLElement
    this._domListeners = []; // [{ target, event, handler }]
    this._selectedStaffIdx = 0; // mobile-only: which staff column is visible
  }

  mount(parent) {
    this.element = document.createElement("div");
    this.element.className =
      "calendar-day-view bg-white rounded-lg border border-gray-200 overflow-x-auto";
    parent.appendChild(this.element);
    this._render();
  }

  setData() {
    if (!this.element) return;
    this._render();
  }

  destroy() {
    this._domListeners.forEach(({ target, event, handler }) => {
      target.removeEventListener(event, handler);
    });
    this._domListeners = [];
    this.eventLayers.clear();
    if (this.element?.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }

  // ---- internal ----

  _render() {
    this._tearDownDomListeners();
    this.eventLayers.clear();
    this.element.replaceChildren();

    if (this.scheduler.staff.length === 0) {
      const empty = document.createElement("div");
      empty.className = "p-12 text-center text-gray-500";
      empty.innerHTML = `<p class="text-sm">No staff members configured.</p>`;
      this.element.appendChild(empty);
      return;
    }

    if (this.scheduler.compact) {
      this._renderMobile();
    } else {
      this._renderDesktop();
    }
  }

  _renderDesktop() {
    const gridTemplateColumns = `var(--calendar-time-column-width) repeat(${this.scheduler.staff.length}, minmax(var(--calendar-min-resource-width), 1fr))`;

    this.element.appendChild(this._buildHeader(gridTemplateColumns));
    this.element.appendChild(this._buildBody(gridTemplateColumns));
    this._paintEvents();
  }

  // Mobile: render only the selected staff's column with a picker (prev/staff/next)
  // above. Touch swipe cycles through staff. Drag/resize naturally disabled
  // on touch (mouse-only mousedown handlers).
  _renderMobile() {
    // Clamp the index to valid range (in case staff list changed under us).
    if (this._selectedStaffIdx >= this.scheduler.staff.length) {
      this._selectedStaffIdx = 0;
    }
    const member = this.scheduler.staff[this._selectedStaffIdx];

    this.element.appendChild(this._buildMobilePicker(member));
    this.element.appendChild(this._buildMobileBody(member));
    this._paintEvents();
    this._enableSwipe();
  }

  _buildMobilePicker(member) {
    const picker = document.createElement("div");
    picker.className =
      "flex items-center justify-between border-b border-gray-200 p-3 bg-white sticky top-0";

    const prev = document.createElement("button");
    prev.type = "button";
    prev.className =
      "px-3 py-2 text-sm font-medium text-gray-600 hover:text-black";
    prev.style.minHeight = "44px";
    prev.style.minWidth = "44px";
    prev.textContent = "‹";
    prev.setAttribute("aria-label", "Previous staff");
    const onPrev = () => this._cycleStaff(-1);
    prev.addEventListener("click", onPrev);
    this._domListeners.push({ target: prev, event: "click", handler: onPrev });

    const label = document.createElement("div");
    label.className = "flex-1 text-center";
    label.innerHTML = `
      <div class="text-xs text-gray-500">${this._selectedStaffIdx + 1} of ${this.scheduler.staff.length}</div>
      <div class="text-sm font-bold text-black">${escapeHtml(member.name || "")}</div>
    `;

    const next = document.createElement("button");
    next.type = "button";
    next.className =
      "px-3 py-2 text-sm font-medium text-gray-600 hover:text-black";
    next.style.minHeight = "44px";
    next.style.minWidth = "44px";
    next.textContent = "›";
    next.setAttribute("aria-label", "Next staff");
    const onNext = () => this._cycleStaff(+1);
    next.addEventListener("click", onNext);
    this._domListeners.push({ target: next, event: "click", handler: onNext });

    picker.append(prev, label, next);
    return picker;
  }

  _buildMobileBody(member) {
    const gridTemplateColumns = `var(--calendar-time-column-width) 1fr`;
    const body = document.createElement("div");
    body.className = "relative grid";
    body.style.gridTemplateColumns = gridTemplateColumns;
    body.dataset.role = "grid";

    const slots = this._timeSlots();

    slots.forEach((slot, rowIdx) =>
      body.appendChild(this._buildTimeLabel(slot, rowIdx))
    );

    slots.forEach((slot, rowIdx) =>
      body.appendChild(this._buildSlotCell(member, slot, 0, rowIdx))
    );
    const layer = this._buildEventLayer(member, 0, slots.length);
    this.eventLayers.set(String(member.id), layer);
    body.appendChild(layer);

    return body;
  }

  _cycleStaff(direction) {
    const count = this.scheduler.staff.length;
    if (count <= 1) return;
    this._selectedStaffIdx =
      (this._selectedStaffIdx + direction + count) % count;
    this._render();
  }

  _enableSwipe() {
    let touchStartX = 0;
    let touchStartY = 0;
    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      touchStartX = t.clientX;
      touchStartY = t.clientY;
    };
    const onEnd = (e) => {
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      // Horizontal swipe with intent (avoids confusion with vertical scroll)
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this._cycleStaff(dx < 0 ? +1 : -1);
    };
    this.element.addEventListener("touchstart", onStart, { passive: true });
    this.element.addEventListener("touchend", onEnd, { passive: true });
    this._domListeners.push(
      { target: this.element, event: "touchstart", handler: onStart },
      { target: this.element, event: "touchend", handler: onEnd }
    );
  }

  _buildHeader(gridTemplateColumns) {
    const header = document.createElement("div");
    header.className = "grid border-b border-gray-200";
    header.style.gridTemplateColumns = gridTemplateColumns;
    header.style.backgroundColor = "var(--calendar-header-bg)";

    const timeHeader = document.createElement("div");
    timeHeader.className = "p-3 font-bold text-gray-500 border-r border-gray-200";
    timeHeader.style.fontSize = "var(--calendar-time-label-size)";
    timeHeader.textContent = "TIME";
    header.appendChild(timeHeader);

    this.scheduler.staff.forEach((member) => {
      header.appendChild(this._buildStaffHeader(member));
    });

    return header;
  }

  _buildStaffHeader(member) {
    const cell = document.createElement("div");
    cell.className = "p-3 border-r border-gray-200 text-center";
    cell.dataset.staffId = String(member.id);

    const avatarHtml = member.avatarUrl
      ? `<img src="${escapeHtml(member.avatarUrl)}"
              alt="${escapeHtml(member.name || "")}"
              class="w-10 h-10 rounded-full mx-auto mb-1 object-cover border-2 border-gray-200">`
      : `<div class="w-10 h-10 rounded-full mx-auto mb-1 bg-black text-white flex items-center justify-center text-xs font-bold">
           ${escapeHtml(staffInitials(member.name))}
         </div>`;

    cell.innerHTML = `
      ${avatarHtml}
      <div class="text-xs font-bold text-black">${escapeHtml(member.name || "")}</div>
    `;
    return cell;
  }

  _buildBody(gridTemplateColumns) {
    const body = document.createElement("div");
    body.className = "relative grid";
    body.style.gridTemplateColumns = gridTemplateColumns;
    body.dataset.role = "grid";

    const slots = this._timeSlots();

    // Time column labels (column 1) — every cell gets an explicit grid-row so
    // CSS Grid's auto-placement cursor doesn't drift across staff columns.
    slots.forEach((slot, rowIdx) =>
      body.appendChild(this._buildTimeLabel(slot, rowIdx))
    );

    // Per-staff column: clickable empty slot cells + absolute event layer
    this.scheduler.staff.forEach((member, colIdx) => {
      slots.forEach((slot, rowIdx) =>
        body.appendChild(this._buildSlotCell(member, slot, colIdx, rowIdx))
      );
      const layer = this._buildEventLayer(member, colIdx, slots.length);
      this.eventLayers.set(String(member.id), layer);
      body.appendChild(layer);
    });

    return body;
  }

  _buildTimeLabel(slot, rowIdx) {
    const cell = document.createElement("div");
    cell.className =
      "border-r border-b flex items-start justify-center pt-1 text-gray-500";
    cell.style.height = "var(--calendar-slot-height)";
    cell.style.borderColor = "var(--calendar-grid-line)";
    cell.style.fontSize = "var(--calendar-time-label-size)";
    cell.style.gridColumn = "1";
    cell.style.gridRow = String(rowIdx + 1);
    cell.textContent = slot.label;
    return cell;
  }

  _buildSlotCell(member, slot, colIdx, rowIdx) {
    const cell = document.createElement("div");
    cell.className =
      "calendar-slot border-r border-b cursor-pointer transition-colors";
    cell.style.height = "var(--calendar-slot-height)";
    cell.style.borderColor = "var(--calendar-grid-line)";
    cell.style.gridColumn = String(colIdx + 2);
    cell.style.gridRow = String(rowIdx + 1);
    cell.dataset.staffId = String(member.id);
    cell.dataset.startTime = slot.startIso;

    const enter = () => {
      cell.style.backgroundColor = "var(--calendar-empty-slot-hover)";
    };
    const leave = () => {
      cell.style.backgroundColor = "";
    };
    const click = (event) => this._handleSlotClick(event, member, slot);

    cell.addEventListener("mouseenter", enter);
    cell.addEventListener("mouseleave", leave);
    cell.addEventListener("click", click);
    this._domListeners.push(
      { target: cell, event: "mouseenter", handler: enter },
      { target: cell, event: "mouseleave", handler: leave },
      { target: cell, event: "click", handler: click }
    );

    return cell;
  }

  _buildEventLayer(member, colIdx, totalRows) {
    const layer = document.createElement("div");
    layer.className = "calendar-event-layer relative";
    layer.dataset.staffId = String(member.id);
    layer.style.gridColumn = String(colIdx + 2);
    layer.style.gridRow = `1 / ${totalRows + 1}`;
    layer.style.pointerEvents = "none";
    return layer;
  }

  _paintEvents() {
    this.eventLayers.forEach((layer) => layer.replaceChildren());

    this.scheduler.events.forEach((item) => {
      const layer = this.eventLayers.get(String(item.staffId));
      if (!layer) return;
      const node = this._buildEventNode(item);
      layer.appendChild(node);
    });
  }

  _buildEventNode(item) {
    const { topPx, heightPx } = this._positionFor(item);
    const node = document.createElement("button");
    node.type = "button";
    node.className =
      "calendar-event absolute left-1 right-1 border-l-4 shadow-sm text-left px-2 py-0.5 overflow-hidden hover:shadow-md transition-shadow";
    node.style.pointerEvents = "auto";
    node.style.top = `${topPx}px`;
    node.style.height = `${heightPx}px`;
    node.style.fontSize = "var(--calendar-event-text-size)";
    node.style.borderRadius = "var(--calendar-event-radius)";
    node.style.backgroundColor =
      item.kind === "block"
        ? "var(--calendar-block-bg)"
        : "var(--calendar-event-bg)";
    node.style.borderLeftColor = `var(${statusCssVariable(item)})`;
    node.dataset.eventId = String(item.id);
    node.dataset.kind = item.kind;
    node.dataset.status = item.status || "";
    node.dataset.locked = item.kind === "block" ? "true" : "false";
    node.innerHTML = item.bodyHtml || "";

    const click = (event) => {
      event.stopPropagation();
      this._handleEventClick(event, item);
    };
    node.addEventListener("click", click);
    this._domListeners.push({ target: node, event: "click", handler: click });

    if (item.kind !== "block") {
      this._enableDrag(node, item);
      node.style.cursor = "grab";
      this._appendResizeHandle(node, item);
    }

    return node;
  }

  // 6px bottom edge handle. Mousedown stops propagation so it doesn't trigger
  // the parent drag-to-move handler.
  _appendResizeHandle(node, item) {
    const handle = document.createElement("span");
    handle.className =
      "calendar-resize-handle absolute bottom-0 left-0 right-0";
    handle.style.height = "6px";
    handle.style.cursor = "ns-resize";
    handle.style.pointerEvents = "auto";
    handle.dataset.resizeHandle = "true";
    node.appendChild(handle);
    this._enableResize(handle, node, item);
  }

  _enableResize(handle, node, item) {
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const state = {
        startY: e.clientY,
        originalHeight: parseInt(node.style.height, 10) || 0,
        moved: false,
      };

      const onMove = (ev) => this._onResizeMove(ev, node, state);
      const onUp = (ev) =>
        this._onResizeEnd(ev, node, item, state, onMove, onUp);
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.userSelect = "none";
    };

    handle.addEventListener("mousedown", onMouseDown);
    this._domListeners.push({
      target: handle,
      event: "mousedown",
      handler: onMouseDown,
    });
  }

  _onResizeMove(e, node, state) {
    const dy = e.clientY - state.startY;

    if (!state.moved && Math.abs(dy) < 5) return;
    state.moved = true;

    const layout = this.scheduler.layout;
    const quarterPx = layout.slotHeightPx / 2;
    const snappedDy = Math.round(dy / quarterPx) * quarterPx;
    // 15-min minimum height = quarterPx
    const newHeight = Math.max(quarterPx, state.originalHeight + snappedDy);
    node.style.height = `${newHeight}px`;
  }

  _onResizeEnd(e, node, item, state, onMove, onUp) {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.userSelect = "";

    if (!state.moved) return;

    // Suppress the click that would otherwise fire after mouseup.
    const suppressClick = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", suppressClick, true);
    };
    window.addEventListener("click", suppressClick, true);

    const layout = this.scheduler.layout;
    const newTop = parseInt(node.style.top, 10) || 0;
    const newHeight = parseInt(node.style.height, 10) || 0;
    const endMinutesOfDay =
      this.scheduler.startHour * 60 +
      ((newTop + newHeight) / layout.slotHeightPx) * layout.slotMinutes;

    // Same date as item.start — resize never changes the date.
    const startDateStr = String(item.start).split("T")[0];
    const newEnd = isoFromMinutesOnDate(startDateStr, endMinutesOfDay);

    this.scheduler._emit("event-resize", {
      id: String(item.id),
      kind: item.kind,
      originalEnd: item.end,
      newEnd,
    });
  }

  // Mouse-only drag-to-move. Vertical drag changes the start time (snapped to
  // 15-min). Dragging horizontally over a different staff column re-parents
  // the node to that column's event layer; on drop, the dragged staffId
  // changes accordingly.
  //
  // Calendar blocks (data-locked="true") never get this hooked up — see
  // `_buildEventNode`. The package never persists; consumers listen to
  // `calendar:event-move` and decide what to do.
  _enableDrag(node, item) {
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target?.dataset?.resizeHandle === "true") return;
      e.preventDefault();

      const layer = node.parentElement;
      const state = {
        startX: e.clientX,
        startY: e.clientY,
        originalTop: parseInt(node.style.top, 10) || 0,
        originalLayer: layer,
        currentLayer: layer,
        moved: false,
      };

      const onMove = (ev) => this._onDragMove(ev, node, state);
      const onUp = (ev) => this._onDragEnd(ev, node, item, state, onMove, onUp);
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.userSelect = "none";
      node.style.cursor = "grabbing";
    };

    node.addEventListener("mousedown", onMouseDown);
    this._domListeners.push({
      target: node,
      event: "mousedown",
      handler: onMouseDown,
    });
  }

  _onDragMove(e, node, state) {
    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;

    if (!state.moved && Math.hypot(dx, dy) < 5) return;
    if (!state.moved) {
      state.moved = true;
      node.style.opacity = "0.7";
      node.style.zIndex = "10";
      node.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
      // Make the dragged node transparent to elementFromPoint so we can find
      // the column beneath the cursor.
      node.style.pointerEvents = "none";
    }

    // Snap to 15-min increments (half a slot at default 30-min slot).
    const layout = this.scheduler.layout;
    const quarterPx = layout.slotHeightPx / 2;
    const snappedDeltaY = Math.round(dy / quarterPx) * quarterPx;
    const newTop = Math.max(0, state.originalTop + snappedDeltaY);
    node.style.top = `${newTop}px`;

    // Cross-column re-parenting: find any ancestor with [data-staff-id]
    // (slot cell, header, or layer all carry it) and re-home the node into
    // that staff's event layer.
    const target = document.elementFromPoint(e.clientX, e.clientY);
    const staffEl = target?.closest?.("[data-staff-id]");
    const targetStaffId = staffEl?.dataset?.staffId;
    if (
      targetStaffId &&
      targetStaffId !== state.currentLayer.dataset.staffId
    ) {
      const targetLayer = this.eventLayers.get(String(targetStaffId));
      if (targetLayer) {
        targetLayer.appendChild(node);
        state.currentLayer = targetLayer;
      }
    }
  }

  _onDragEnd(e, node, item, state, onMove, onUp) {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.userSelect = "";
    node.style.cursor = "grab";

    if (!state.moved) return; // plain click — let the click handler fire

    node.style.opacity = "";
    node.style.zIndex = "";
    node.style.boxShadow = "";
    // `pointer-events` is inherited; the parent layer is `none`, so we must
    // explicitly restore "auto" rather than "" (which would un-set the inline
    // value and let the button inherit `none`, making it uninteractive).
    node.style.pointerEvents = "auto";

    // Suppress the native click event that fires after a mouseup post-drag.
    const suppressClick = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", suppressClick, true);
    };
    window.addEventListener("click", suppressClick, true);

    const newTop = parseInt(node.style.top, 10) || 0;
    const layout = this.scheduler.layout;
    const startMinutesOfDay =
      this.scheduler.startHour * 60 +
      (newTop / layout.slotHeightPx) * layout.slotMinutes;
    const durationMinutes =
      minutesFromMidnight(item.end) - minutesFromMidnight(item.start);

    const newStart = isoFromMinutesOnDate(
      this.scheduler.date,
      startMinutesOfDay
    );
    const newEnd = isoFromMinutesOnDate(
      this.scheduler.date,
      startMinutesOfDay + durationMinutes
    );

    this.scheduler._emit("event-move", {
      id: String(item.id),
      kind: item.kind,
      originalStart: item.start,
      newStart,
      originalEnd: item.end,
      newEnd,
      originalStaffId: state.originalLayer.dataset.staffId,
      newStaffId: state.currentLayer.dataset.staffId,
    });
  }

  _positionFor(item) {
    const layout = this.scheduler.layout;
    const dayStartMinutes = this.scheduler.startHour * 60;
    const startMinutes = minutesFromMidnight(item.start);
    const endMinutes = minutesFromMidnight(item.end);
    const topPx =
      ((startMinutes - dayStartMinutes) / layout.slotMinutes) *
      layout.slotHeightPx;
    const heightPx =
      ((endMinutes - startMinutes) / layout.slotMinutes) *
      layout.slotHeightPx;
    return { topPx: Math.max(0, topPx), heightPx: Math.max(8, heightPx) };
  }

  _timeSlots() {
    const slots = [];
    const startHour = this.scheduler.startHour;
    const endHour = this.scheduler.endHour;
    const minuteSteps = [0, 30];
    for (let hour = startHour; hour < endHour; hour++) {
      minuteSteps.forEach((minute) => {
        slots.push({
          hour,
          minute,
          label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
          startIso: isoFromMinutesOnDate(this.scheduler.date, hour * 60 + minute),
        });
      });
    }
    return slots;
  }

  _handleSlotClick(event, member, slot) {
    const totalMinutes = slot.hour * 60 + slot.minute;
    this.scheduler._emit("slot-click", {
      staffId: String(member.id),
      startTime: slot.startIso,
      endTime: isoFromMinutesOnDate(
        this.scheduler.date,
        totalMinutes + this.scheduler.layout.slotMinutes
      ),
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  _handleEventClick(event, item) {
    this.scheduler._emit("event-click", {
      kind: item.kind,
      id: String(item.id),
      appointmentId: item.kind === "appointment" ? String(item.id) : null,
      blockId: item.kind === "block" ? String(item.id) : null,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  _tearDownDomListeners() {
    this._domListeners.forEach(({ target, event, handler }) =>
      target.removeEventListener(event, handler)
    );
    this._domListeners = [];
  }
}
